import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Icon, Box, Card, CardContent} from "@material-ui/core";
import PageHeader from "../../shared-components/page-header/PageHeader";
import FlightService from "../../../services/flight.services";
import {errorMsg, successMsg} from "../../../utils/MessageUtils";
import {FlightImportResponse} from "../../../types/service-types/FlightTypes";
import {useDispatch} from "react-redux";

const ImportFlightsCsvPage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importResults, setImportResults] = useState<FlightImportResponse | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            dispatch(errorMsg(t("app.main.configurations.importFlightsCsv.invalidFile", "Please select a CSV file")));
            return;
        }

        setUploading(true);
        try {
            const response = await FlightService.importFromCsv(file);
            setImportResults(response);
            setImportDialogOpen(true);

            if (response.successfulImports > 0) {
                dispatch(successMsg(
                    t("app.main.configurations.importFlightsCsv.success", 
                      "Successfully imported {{count}} flight(s)", 
                      {count: response.successfulImports})
                ));
            }
            
            if (response.failedImports > 0) {
                dispatch(errorMsg(
                    t("app.main.configurations.importFlightsCsv.partialFailure", 
                      "{{count}} flight(s) failed to import", 
                      {count: response.failedImports})
                ));
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || 
                               error?.message || 
                               t("app.main.configurations.importFlightsCsv.error", "Failed to import flights from CSV");
            dispatch(errorMsg(errorMessage));
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleCloseImportDialog = () => {
        setImportDialogOpen(false);
        setImportResults(null);
    };

    const handleDownloadExample = () => {
        // Create CSV content
        const csvContent = [
            csvHeaders.join(','),
            ...csvExampleData.map(row => 
                csvHeaders.map(header => {
                    const value = row[header] || '';
                    // Escape commas and quotes in values
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'flights_import_example.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Example CSV data based on the provided file
    const csvExampleData: Record<string, string>[] = [
        {
            FlightNumber: "PJ333323",
            DepartureAirportCode: "PRN",
            ArrivalAirportCode: "MUC",
            DepartureDate: "15/01/2026",
            DepartureTime: "10:00",
            ArrivalTime: "12:30",
            TotalAircraftSeats: "168",
            MaxNumberOfTravelers: "30",
            Gepack: "20",
            GepackINF: "5",
            MaxOnline: "30",
            TaxOfFlight: "0",
            IncreasePriceOfOnlineBookingFor: "5",
            AirlineCompanyName: "GP-Aviation",
            CurrencyIsoCode: "EUR",
            Gate: "",
            PublicAvailability: "TRUE",
            Sector: "1",
            SectorName: "Economy",
            SectorNumberOfSeats: "30",
            OneWayPriceAdult: "500",
            OneWayPriceChild: "500",
            OneWayPriceInfant: "500",
            ReturnPriceAdult: "500",
            ReturnPriceChild: "500",
            ReturnPriceInfant: "500",
            RecurringFrequencyInDays: "0",
            RecurringEndDate: ""
        }
    ];

    const csvHeaders = [
        "FlightNumber", "DepartureAirportCode", "ArrivalAirportCode", "DepartureDate", "DepartureTime", 
        "ArrivalTime", "TotalAircraftSeats", "MaxNumberOfTravelers", "Gepack", "GepackINF", "MaxOnline", 
        "TaxOfFlight", "IncreasePriceOfOnlineBookingFor", "AirlineCompanyName", "CurrencyIsoCode", 
        "Gate", "PublicAvailability", "Sector", "SectorName", "SectorNumberOfSeats", 
        "OneWayPriceAdult", "OneWayPriceChild", "OneWayPriceInfant", "ReturnPriceAdult", 
        "ReturnPriceChild", "ReturnPriceInfant", "RecurringFrequencyInDays", "RecurringEndDate"
    ];

    return (
        <div>
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />
            <PageHeader
                title={t("app.main.configurations.importFlightsCsv.title", "Add flights via CSV file")}
                desc={t("app.main.configurations.importFlightsCsv.desc", "Upload a CSV file to import multiple flights at once")}
                button={uploading 
                    ? t("app.main.configurations.importFlightsCsv.uploading", "Uploading...")
                    : t("app.main.configurations.importFlightsCsv.uploadButton", "Upload CSV")
                }
                onClickButton={handleUploadButtonClick}
            />
            
            <Box style={{ marginTop: '30px', padding: '20px' }}>
                <Card style={{ marginBottom: '30px' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom style={{ marginBottom: '15px' }}>
                            <Icon style={{ verticalAlign: 'middle', marginRight: '8px' }}>info</Icon>
                            {t("app.main.configurations.importFlightsCsv.exampleTitle", "CSV File Format Example")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            {t("app.main.configurations.importFlightsCsv.exampleDesc", 
                               "Your CSV file should follow this format. The first row must contain the column headers, and subsequent rows should contain the flight data.")}
                        </Typography>
                        
                        <Box style={{ marginTop: '15px', marginBottom: '15px' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Icon>download</Icon>}
                                onClick={handleDownloadExample}
                                style={{background: '#db0001'}}
                            >
                                {t("app.main.configurations.importFlightsCsv.downloadExample", "Download CSV Example")}
                            </Button>
                        </Box>
                        
                        <TableContainer component={Paper} style={{ marginTop: '15px', maxHeight: '500px', overflow: 'auto' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {csvHeaders.map((header, index) => (
                                                <TableCell key={index} style={{ 
                                                    fontWeight: 600, 
                                                    backgroundColor: '#f5f5f5',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '0.7rem',
                                                    padding: '8px 4px'
                                                }}>
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvExampleData.map((row, rowIndex) => (
                                            <TableRow key={rowIndex}>
                                                {csvHeaders.map((header, colIndex) => (
                                                    <TableCell key={colIndex} style={{ 
                                                        fontSize: '0.7rem',
                                                        padding: '8px 4px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {row[header] || ''}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>

            {/* Import Results Dialog */}
            <Dialog
                open={importDialogOpen}
                onClose={handleCloseImportDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {t("app.main.configurations.importFlightsCsv.resultsTitle", "Import Results")}
                </DialogTitle>
                <DialogContent dividers>
                    {importResults && (
                        <div>
                            <Typography variant="body1" gutterBottom>
                                <strong>{t("app.main.configurations.importFlightsCsv.totalRecords", "Total Records")}:</strong> {importResults.totalRecords}
                            </Typography>
                            <Typography variant="body1" gutterBottom style={{ color: '#4caf50' }}>
                                <strong>{t("app.main.configurations.importFlightsCsv.successfulImports", "Successful Imports")}:</strong> {importResults.successfulImports}
                            </Typography>
                            <Typography variant="body1" gutterBottom style={{ color: '#f44336' }}>
                                <strong>{t("app.main.configurations.importFlightsCsv.failedImports", "Failed Imports")}:</strong> {importResults.failedImports}
                            </Typography>

                            {importResults.errors && importResults.errors.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <Typography variant="h6" gutterBottom>
                                        {t("app.main.configurations.importFlightsCsv.errors", "Errors")}:
                                    </Typography>
                                    <TableContainer component={Paper} style={{ maxHeight: '400px', overflow: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>{t("app.main.configurations.importFlightsCsv.rowNumber", "Row")}</strong></TableCell>
                                                    <TableCell><strong>{t("app.main.configurations.importFlightsCsv.flightNumber", "Flight Number")}</strong></TableCell>
                                                    <TableCell><strong>{t("app.main.configurations.importFlightsCsv.departureDate", "Departure Date")}</strong></TableCell>
                                                    <TableCell><strong>{t("app.main.configurations.importFlightsCsv.errorMessage", "Error")}</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {importResults.errors.map((error, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{error.rowNumber}</TableCell>
                                                        <TableCell>{error.flightNumber}</TableCell>
                                                        <TableCell>{error.departureDate}</TableCell>
                                                        <TableCell style={{ color: '#f44336' }}>{error.errorMessage}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImportDialog} color="primary">
                        {t("app.common.close", "Close")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ImportFlightsCsvPage;

